import { useEffect, useRef, useState, type ReactElement } from 'react';

/**
 * Voice input for the composer (§4.5, 2026-09-02): record one clip in the
 * browser, hand the bytes back. This is dictation, not the voice call — the
 * clip becomes words in the field, nothing more.
 *
 * MediaRecorder picks the container the browser can actually produce (webm/
 * opus on Chrome and Firefox, mp4 on Safari); the Blob carries that type and
 * the edge routes on it. An AnalyserNode rides the same stream so the
 * waveform shows the person they are being heard.
 */

export type MicUnavailableReason = 'denied' | 'unsupported';

const CONTAINERS = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];

function pickContainer(): string {
  if (typeof MediaRecorder === 'undefined') return '';
  return CONTAINERS.find((t) => MediaRecorder.isTypeSupported(t)) ?? '';
}

interface Live {
  stream: MediaStream;
  recorder: MediaRecorder;
  context: AudioContext;
  chunks: Blob[];
}

export interface VoiceRecorder {
  recording: boolean;
  /** Live while recording, for the waveform. */
  analyser: AnalyserNode | null;
  /** Ask for the mic and start. Rejects with a MicUnavailableReason. */
  start: () => Promise<void>;
  /** Stop and resolve the clip. */
  finish: () => Promise<Blob>;
  /** Stop and drop everything. */
  cancel: () => void;
}

export function useVoiceRecorder(): VoiceRecorder {
  const liveRef = useRef<Live | null>(null);
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);

  const release = (): void => {
    const live = liveRef.current;
    liveRef.current = null;
    setAnalyser(null);
    if (!live) return;
    for (const track of live.stream.getTracks()) track.stop();
    void live.context.close().catch(() => undefined);
  };

  // Leaving the page mid-recording must not leave the mic light on.
  useEffect(() => release, []);

  const start = async (): Promise<void> => {
    if (liveRef.current) return;
    const container = pickContainer();
    if (!container || !navigator.mediaDevices?.getUserMedia) {
      throw new Error('unsupported' satisfies MicUnavailableReason);
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      throw new Error('denied' satisfies MicUnavailableReason);
    }
    const context = new AudioContext();
    const node = context.createAnalyser();
    node.fftSize = 1024;
    context.createMediaStreamSource(stream).connect(node);
    const recorder = new MediaRecorder(stream, { mimeType: container });
    const chunks: Blob[] = [];
    recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
    recorder.start(250);
    liveRef.current = { stream, recorder, context, chunks };
    setAnalyser(node);
  };

  const finish = (): Promise<Blob> => new Promise((resolve, reject) => {
    const live = liveRef.current;
    if (!live) { reject(new Error('not_recording')); return; }
    const type = live.recorder.mimeType || 'audio/webm';
    live.recorder.onstop = () => {
      release();
      resolve(new Blob(live.chunks, { type }));
    };
    live.recorder.stop();
  });

  const cancel = (): void => {
    const live = liveRef.current;
    if (live && live.recorder.state !== 'inactive') {
      live.recorder.onstop = null;
      live.recorder.stop();
    }
    release();
  };

  return { recording: analyser !== null, analyser, start, finish, cancel };
}

/**
 * The scrolling bar waveform of a voice memo: one bar per ~50ms of loudness,
 * newest at the right, drawn in the composer's text colour.
 */
export function VoiceWave({ analyser }: { analyser: AnalyserNode }): ReactElement {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;
    const data = new Uint8Array(analyser.fftSize);
    const bars: number[] = [];
    const BAR = 3;
    const GAP = 2;
    let lastPush = 0;
    let frame = 0;
    const color = getComputedStyle(canvas).color;

    const draw = (now: number): void => {
      const dpr = window.devicePixelRatio || 1;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
        canvas.width = Math.round(w * dpr);
        canvas.height = Math.round(h * dpr);
      }
      analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i += 1) {
        const v = (data[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / data.length);
      if (now - lastPush >= 50) {
        lastPush = now;
        bars.push(Math.min(1, rms * 4));
        const capacity = Math.ceil(w / (BAR + GAP)) + 1;
        if (bars.length > capacity) bars.splice(0, bars.length - capacity);
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = color;
      let x = w - BAR;
      for (let i = bars.length - 1; i >= 0 && x > -BAR; i -= 1) {
        const bh = Math.max(3, bars[i] * h);
        const y = (h - bh) / 2;
        ctx.beginPath();
        ctx.roundRect(x, y, BAR, bh, BAR / 2);
        ctx.fill();
        x -= BAR + GAP;
      }
      frame = requestAnimationFrame(draw);
    };
    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, [analyser]);

  return <canvas ref={canvasRef} className="convo-mic-wave" aria-label="Recording" />;
}
